import { Dayjs } from "dayjs";
import { v4 as uuidv4 } from "uuid";
import {
  DebtCardData,
  DebtDocument,
  DraftDebt,
  debtDocumentStatus,
  debtDocumentType,
  debtErrorMessage,
  debtType,
} from "../../interfaces/interfaces";

// Pure helpers for the debt cards: no React, no API. The rules that decide
// what blocks Next or the final submit live in debtValidation.ts.

// the backend stores actualStartDate as sent; this is the format we send and read back
export const START_DATE_FORMAT = "YYYY-MM-DD";

// The date field reports a value while the user is still typing it; a year of
// "2" is a valid dayjs date (year 0002), so treat anything before 1900 as unfinished.
export const isCompleteDate = (date: Dayjs | null): date is Dayjs =>
  !!date && date.isValid() && date.year() >= 1900;

export const debtTypeOptions = [
  { label: "Term", value: debtType.term },
  { label: "Revolving", value: debtType.revolving },
];

export const getDebtTypeLabel = (value: debtType) =>
  debtTypeOptions.find((option) => option.value === value)?.label ?? value;

// a draft card: no debtId until its sanction letter is uploaded, so it gets a
// clientId to be told apart from other cards until then
export const getDefaultDebt = (): DraftDebt => ({
  clientId: uuidv4(),
  debtType: debtType.term,
});

// narrows a card to a draft; anything calling the API with debtId checks this first
export const isDraftDebt = (debt: DebtCardData): debt is DraftDebt => !debt?.debtId;

// Draft cards live only in the browser: the server drops them on save, so its
// copy of the form never has them. Carry them over from the browser's copy
// (the local draft, or the values just submitted) whichever copy won, then
// make sure a "Yes" always shows at least one card.
export const withBrowserDebts = (
  values: Record<string, any>,
  browserValues?: Record<string, any> | null
) => {
  if (values?.hasDebt != "yes") return values;
  const debts: DebtCardData[] = values?.debt || [];
  // when the browser's copy won, its drafts are already in the list
  const clientIds = new Set(debts.map((debt) => debt?.clientId).filter(Boolean));
  const browserDrafts = ((browserValues?.debt || []) as DebtCardData[]).filter(
    (debt) => isDraftDebt(debt) && !clientIds.has(debt.clientId)
  );
  const merged = [...debts, ...browserDrafts];
  return { ...values, debt: merged.length ? merged : [getDefaultDebt()] };
};

export type ClassificationTag = { label: string; found: boolean };

// what the classifier found inside the PDF, shown as tags under the file name;
// showMissingSchedule adds a red ✗ tag when a term loan still needs its schedule
export const getClassificationTags = (
  document: DebtDocument,
  showMissingSchedule: boolean
): ClassificationTag[] => {
  const tags: ClassificationTag[] = [];
  if (document.sanctionLetter) tags.push({ label: "Sanction Letter", found: true });
  if (document.repaymentSchedule) {
    tags.push({ label: "Repayment Schedule", found: true });
  } else if (showMissingSchedule && document.jobType === debtDocumentType.sanctionLetter) {
    tags.push({ label: "Repayment Schedule", found: false });
  }
  return tags;
};

export type DocumentError = { title: string; action?: string };

// Only a failed job shows an error. The backend writes the wording into
// errorState ({ title, action }), the same shape the financial statements card
// renders; a failure without a message gets a generic one so a broken file
// never sits there unexplained.
export const getDocumentError = (document: DebtDocument): DocumentError | null => {
  if (document.status !== debtDocumentStatus.failed) return null;
  if (document.errorState?.title) {
    return { title: document.errorState.title, action: document.errorState.action };
  }
  return {
    title: debtErrorMessage.unreadableFileTitle,
    action: debtErrorMessage.unreadableFileAction,
  };
};
